import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { VNC_API_BASE } from '../../../../../components/CameraSlot/constants';
import { CAMERA_PANEL_COMMAND_EVENT } from '../../../HudPanelComponents/constants';

export default function useStatisticsCameras({ datasetKey, cameraNames, initialTimestamp }) {
  const [enabledDataset, setEnabledDataset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const previewTimer = useRef(null);
  const lastTimestamp = useRef(null);
  const requestId = useRef(0);
  const enabled = enabledDataset !== null && enabledDataset === datasetKey;
  const cancelPreview = () => clearTimeout(previewTimer.current);
  const send = (action, timestamp, cameras) =>
    window.dispatchEvent(
      new CustomEvent(CAMERA_PANEL_COMMAND_EVENT, {
        detail: { action, timestamp, cameras, requestId: ++requestId.current }
      })
    );

  useEffect(() => {
    if (!enabled) {
      setEnabledDataset(null);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const loadCameras = async () => {
      const backendCameras = [];
      try {
        let url = `${VNC_API_BASE}/cameras/`;
        while (url) {
          const response = await axios.get(url, { signal: controller.signal });
          if (controller.signal.aborted) return;
          backendCameras.push(...(response.data.results ?? response.data));
          const next = response.data.next ? new URL(response.data.next, window.location.origin) : null;
          url = next ? `${next.pathname}${next.search}` : null;
        }
        const cameras = [];
        for (const name of new Set(cameraNames.map((name) => name.trim()))) {
          const matches = backendCameras.filter((camera) => camera.camera_name?.trim() === name);
          const camera = matches[0];
          if (matches.length > 1 || !camera?.ipAddress) {
            setError({ message: matches.length > 1 ? 'statisticsCameraAmbiguous' : 'statisticsCameraMissing', name });
          } else {
            cameras.push({ name, path: camera.ipAddress.trim().replace(/\./g, '_') });
          }
        }
        send('open', lastTimestamp.current, cameras);
      } catch {
        if (!controller.signal.aborted) setError({ message: 'statisticsCameraLoadFailed', name: cameraNames.join(', ') });
      }
      if (!controller.signal.aborted) setLoading(false);
    };
    loadCameras();
    return () => {
      controller.abort();
      cancelPreview();
      send('close');
    };
  }, [enabled, cameraNames]);

  const seek = (value, immediate = false) => {
    cancelPreview();
    const timestamp = Date.parse(value);
    if (!enabled || !Number.isFinite(timestamp) || (!immediate && timestamp === lastTimestamp.current)) return;
    const apply = () => {
      lastTimestamp.current = timestamp;
      send('seek', timestamp);
    };
    if (immediate) apply();
    else previewTimer.current = setTimeout(apply, 200);
  };

  return {
    enabled,
    loading,
    error,
    seek,
    cancelPreview,
    toggle() {
      cancelPreview();
      lastTimestamp.current = Date.parse(initialTimestamp);
      setEnabledDataset(enabled ? null : datasetKey);
    }
  };
}
